import React, { useState, useEffect } from 'react';
import './login-screen.scss';
import { login } from '../../utils/api';
import { saveSession, saveCredentials, getSavedCredentials } from '../../utils/session';

const AR = {
  validation: {
    mobileRequired: 'رقم الموبايل مطلوب',
    mobileInvalid: 'يرجى إدخال رقم موبايل صحيح',
    passwordRequired: 'كلمة المرور مطلوبة',
    passwordMinLength: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
  },
  errors: {
    loginFailed: 'فشل تسجيل الدخول. يرجى التحقق من بياناتك والمحاولة مرة أخرى.',
    unexpected: 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.',
    network: 'خطأ في الاتصال. تحقق من الاتصال بالإنترنت.',
    unauthorized: 'رقم الموبايل أو كلمة المرور غير صحيحة.',
  },
};

interface LoginScreenProps {
  onComplete: (userData: { username: string; fullName: string; mobileNumber: string }) => void;
  onGoToRegister: () => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onComplete, onGoToRegister }) => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');

  // Pre-fill mobile from saved credentials (helps user confirm who they're logging in as)
  useEffect(() => {
    const saved = getSavedCredentials();
    if (saved?.mobile) {
      setMobile(saved.mobile);
    }
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    mobile?: string;
    password?: string;
    general?: string;
  }>({});

  const validateForm = () => {
    const newErrors: { mobile?: string; password?: string } = {};

    if (!mobile.trim()) {
      newErrors.mobile = AR.validation.mobileRequired;
    } else if (!/^\+?[\d\s-()]{8,15}$/.test(mobile.trim())) {
      newErrors.mobile = AR.validation.mobileInvalid;
    }

    if (!password) {
      newErrors.password = AR.validation.passwordRequired;
    } else if (password.length < 6) {
      newErrors.password = AR.validation.passwordMinLength;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getArabicErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      const msg = error.message.toLowerCase();
      if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('networkerror')) {
        return AR.errors.network;
      }
      if (msg.includes('401') || msg.includes('unauthorized') || msg.includes('invalid') || msg.includes('incorrect')) {
        return AR.errors.unauthorized;
      }
      return AR.errors.loginFailed;
    }
    return AR.errors.unexpected;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const response = await login(
        {
          mobile: mobile.trim(),
          password,
        },
        { skipGlobalLoading: true },
      );

      saveSession({
        token: response.token,
        userName: response.userName,
        fullName: response.fullName || '',
        mobile: response.mobile,
        email: response.email,
      });

      // Persist credentials so the app can auto-login on next launch
      saveCredentials(mobile.trim(), password);

      onComplete({
        username: response.userName,
        fullName: response.fullName || '',
        mobileNumber: response.mobile,
      });
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: getArabicErrorMessage(error) });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-screen__glow login-screen__glow--tr" aria-hidden />
      <div className="login-screen__glow login-screen__glow--bl" aria-hidden />
      <div className="login-screen__inner">
        <div className="login-header">
          <div className="login-header__logo-wrap">
            <img src="/new/blue.png" alt="Ameen" className="login-logo" />
          </div>
          <h1>مرحبًا بك في أمين</h1>
          <p className="login-subtitle">يرجى إدخال بياناتك للمتابعة</p>
        </div>

        <form className="auth-form-card" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="error-message general-error" dir="rtl" role="alert">
              {errors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="mobile" className="form-label">
              رقم الموبايل
            </label>
            <input
              type="tel"
              id="mobile"
              className={`form-input ${errors.mobile ? 'error' : ''}`}
              value={mobile}
              onChange={e => {
                setMobile(e.target.value);
                if (errors.mobile) setErrors(prev => ({ ...prev, mobile: undefined }));
              }}
              placeholder="أدخل رقم الموبايل"
              disabled={isLoading}
            />
            {errors.mobile && (
              <span className="error-message" dir="rtl">{errors.mobile}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              كلمة المرور
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors(prev => ({ ...prev, password: undefined }));
                }}
                placeholder="أدخل كلمة المرور"
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(prev => !prev)}
                tabIndex={-1}
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                <span className="material-symbols-outlined">
                  {showPassword ? 'visibility' : 'visibility_off'}
                </span>
              </button>
            </div>
            {errors.password && (
              <span className="error-message" dir="rtl">{errors.password}</span>
            )}
          </div>

          <div className="auth-form-card__forgot-wrap" dir="rtl">
            <button
              type="button"
              className="auth-forgot-password"
              disabled={isLoading}
              onClick={() => {
                /* استعادة كلمة المرور — يمكن ربطها لاحقًا بمسار أو API */
              }}
            >
              نسيت كلمة المرور؟
            </button>
          </div>

          <button type="submit" className="auth-form-card__submit" disabled={isLoading}>
            {isLoading ? (
              <span className="loading-content">
                <span className="spinner" />
                جاري تسجيل الدخول...
              </span>
            ) : (
              'تسجيل الدخول'
            )}
          </button>
        </form>

        <div className="auth-page-footer" dir="rtl">
          <div className="auth-page-footer__row">
            <span className="auth-page-footer__line" aria-hidden />
            <span className="auth-page-footer__hint">أول مرة تستخدم أمين؟</span>
            <span className="auth-page-footer__line" aria-hidden />
          </div>
          <button
            type="button"
            className="auth-page-footer__cta"
            onClick={onGoToRegister}
            disabled={isLoading}
          >
            إنشاء حساب جديد
          </button>
        </div>
      </div>
         <div className=" powerd  ">
           <small> مدعوم بواسطة</small>
            <a href="https://www.eg-insurtech.com/" target="_blank"
                className=" w-100 align-self-center mx-2 d-block am-text-warn-700 font-weight-bold ">
                 <img width="120" className="align-self-center"
                    src="/EG insurtech logo-01.png"/>
            </a>

        </div>
    </div>
  );
};

export default LoginScreen;
